import { Link } from 'react-router-dom'
import { Section, SectionHeading, NumberCircle, FAQ, CTABand, SplitHero, NonMedicalNote } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const steps = [
  { n: '1', title: 'You send a care request', body: 'Call us or fill out a short form. Tell us who needs support and what a hard day looks like right now — nothing formal required.' },
  { n: '2', title: 'We confirm your service area', body: "We serve Calgary and surrounding communities. We'll confirm we can reach your neighbourhood before anything else." },
  { n: '3', title: 'You choose assessment times', body: 'Pick up to three times that suit your family. In-home, at our Brisebois Drive office, or by phone — whatever feels easiest.' },
  { n: '4', title: 'You meet a care coordinator', body: 'A real person listens first: routines, history, worries, preferences. No pressure and no medical jargon.' },
  { n: '5', title: 'We build your 5-Pillar Care Plan', body: 'Goals and lifestyle, family customization, caregiver matching, budget alignment and ongoing communication, written down clearly.' },
  { n: '6', title: 'We match a caregiver', body: "We introduce a screened caregiver chosen for personality and skills fit — and we'll adjust if the match isn't right." },
  { n: '7', title: 'Care begins', body: "Your caregiver follows the plan's task checklists, at the pace your loved one prefers. Care is non-medical throughout." },
  { n: '8', title: 'Family updates arrive in the portal', body: 'Shift notes, task checklists and schedule changes appear in the Family Room after each visit.' },
  { n: '9', title: 'Billing stays clear', body: 'Hours, services and receipts are itemized in the portal. No surprise fees, and plans can change as needs change.' },
]

const pillars = [
  { title: 'Goals, History & Lifestyle', body: 'We start with the person: their story, routines, faith, food, music, pets and what independence means to them.' },
  { title: 'Family-Centered Customization', body: 'Families tell us what matters most, and the plan is written around those priorities — not a fixed package.' },
  { title: 'Caregiver Matching', body: 'We match on temperament, language, experience and schedule so visits feel like a familiar friend arriving.' },
  { title: 'Budget & Pricing Alignment', body: 'We shape hours and services around a budget you set, and we tell you plainly what each option costs.' },
  { title: 'Ongoing Communication', body: 'Shift notes, plan reviews and a direct line to your coordinator keep everyone in the loop as needs change.' },
]

const faqs = [
  { q: 'How fast can care start?', a: 'We can often begin services within 24 to 48 hours of an assessment, depending on caregiver availability and the complexity of the care plan.' },
  { q: 'What happens at the assessment?', a: "A care coordinator visits to listen to your needs, understand routines, and discuss how we can help. It's a no-pressure conversation to see if we're a good fit." },
  { q: 'Can we change the care plan later?', a: 'Absolutely. Plans are flexible and can be updated as needs change. You can request changes through the portal or by calling your coordinator.' },
  { q: 'How does billing work?', a: 'We provide clear, itemized billing through our secure portal. There are no hidden fees or long-term contracts.' },
]

export default function HowItWorks() {
  return (
    <>
      <SplitHero
        eyebrow="The Process"
        title="A simple path to peace of mind at home"
        lede="Starting care shouldn't be complicated. We've designed a clear, nine-step process to ensure your family feels supported and informed every step of the way."
        image="/assets/how-it-works.jpg"
        imageAlt="A daughter and elderly mother looking at a tablet together, smiling"
        actions={
          <>
            <Link to="/schedule-assessment" className="btn btn-primary">Schedule an In-Home Assessment</Link>
            <a href={PHONE_TEL} className="btn btn-secondary">Call {PHONE_DISPLAY}</a>
          </>
        }
      />

      {/* Nine-step timeline */}
      <Section tone="white">
        <SectionHeading
          eyebrow="Step by Step"
          title="Your journey to personalized care"
          lede="From your first phone call to seeing shift notes in the portal, here is exactly what to expect when you work with True North."
        />
        <div style={{ position: 'relative', marginTop: 64, marginLeft: 'clamp(0px, 4vw, 32px)', maxWidth: 768 }}>
          <div style={{ position: 'absolute', left: 23, top: 16, height: 'calc(100% - 32px)', width: 2, background: 'oklch(0.6058 0.0491 154.68 / 20%)' }} aria-hidden="true" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
            {steps.map((s) => (
              <div key={s.n} style={{ display: 'flex', gap: 20, position: 'relative', zIndex: 1 }}>
                <span style={{ marginTop: 4 }}><NumberCircle n={s.n} /></span>
                <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>{s.title}</h3>
                  <p style={{ margin: '8px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'var(--color-muted)' }}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 5-Pillar Care Plan */}
      <Section tone="dark">
        <SectionHeading
          eyebrow="The Foundation"
          title="Our 5-Pillar Care Plan"
          lede="Every client receives a custom care plan built on these core principles to ensure consistency, safety, and joy."
          onDark
        />
        <div className="grid grid-3" style={{ marginTop: 48, gap: 24 }}>
          {pillars.map((p) => (
            <div key={p.title} className="card-teal">
              <h3 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-cream)' }}>{p.title}</h3>
              <p style={{ margin: '16px 0 0 0', fontSize: 17, lineHeight: 1.6, color: 'oklch(0.9765 0.0098 87.47 / 90%)' }}>{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Non-medical disclaimer */}
      <Section tone="panel">
        <div style={{ maxWidth: 768, margin: '0 auto' }}>
          <NonMedicalNote />
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="cream">
        <FAQ items={faqs} />
      </Section>

      <CTABand />
    </>
  )
}
